/** @format */

import { queueDebug } from '../debug';
import { Timer } from './Timer';
import { TimerOptions } from './TimerOptions';
import TimerEvent from './Event/TimerEvent';
import { TimerManagerSubscribeEventEnum } from './Enum/TimerManagerSubscribeEventEnum';
import { TimerManagerDispatchEventEnum } from './Enum/TimerManagerDispatchEventEnum';
import { TimerEventEnum } from './Enum/TimerEventEnum';
import TimerEventDict from './Event/TimerEventDict';

declare global {
    interface Window {
        timerHolder: { [key: string]: { [key: string]: Timer } };
    }
}

export class TimerManager {
    //TODO: Add events for checking how many timers exist (globally/in a namespace) [can we use it to do some visual in the future?]
    private static instance: TimerManager;

    private constructor() {
        if (!window.timerHolder) {
            window.timerHolder = {};
        }

        queueDebug(`Created TimerManager`);
        TimerEvent.getInstance().subscribe(TimerManagerSubscribeEventEnum.createTimer, (e) => {
            this.createTimer(e.detail.namespace, e.detail.timerOptions, e.callback);
        });
        TimerEvent.getInstance().subscribe(TimerManagerSubscribeEventEnum.startTimer, (e) => {
            this.startTimer(e.detail.namespace, e.detail.identifier, e.callback);
        });
        TimerEvent.getInstance().subscribe(TimerManagerSubscribeEventEnum.startTimerWithDelay, (e) => {
            this.startTimerWithDelay(e.detail.namespace, e.detail.identifier, e.detail.delay, e.callback);
        });
        TimerEvent.getInstance().subscribe(TimerManagerSubscribeEventEnum.stopTimer, (e) => {
            this.stopTimer(e.detail.namespace, e.detail.identifier, e.callback);
        });
        TimerEvent.getInstance().subscribe(TimerManagerSubscribeEventEnum.removeTimer, (e) => {
            this.removeTimer(e.detail.namespace, e.detail.identifier, e.callback);
        });
        TimerEvent.getInstance().subscribe(TimerManagerSubscribeEventEnum.stopTimersInNamespace, (e) => {
            this.stopTimersInNamespace(e.detail.namespace, e.callback);
        });
        TimerEvent.getInstance().subscribe(TimerManagerSubscribeEventEnum.removeTimersInNamespace, (e) => {
            this.removeTimersInNamespace(e.detail.namespace, e.callback);
        });
        TimerEvent.getInstance().subscribe(TimerManagerSubscribeEventEnum.stopTimersGlobally, (e) => {
            this.stopTimersGlobally(e.callback);
        });
        TimerEvent.getInstance().subscribe(TimerManagerSubscribeEventEnum.removeTimersGlobally, (e) => {
            this.removeTimersGlobally(e.callback);
        });
        TimerEvent.getInstance().subscribe(TimerManagerSubscribeEventEnum.checkTimerExists, (e) => {
            this.timerExists(e.detail.namespace, e.detail.identifier, e.callback);
        });
        TimerEvent.getInstance().subscribe(TimerManagerSubscribeEventEnum.checkTimerRunning, (e) => {
            this.timerRunning(e.detail.namespace, e.detail.identifier, e.callback);
        });
        TimerEvent.getInstance().subscribe(TimerManagerSubscribeEventEnum.checkTimerExistsAndRunning, (e) => {
            this.timerExistsAndRunning(e.detail.namespace, e.detail.identifier, e.callback);
        });
        TimerEvent.getInstance().subscribe(TimerManagerSubscribeEventEnum.accessMethodOnTimer, (e) => {
            this.accessMethodOnTimer(e.detail.namespace, e.detail.identifier, e.detail.method, e.detail.methodArguments, e.callback);
        });

        this.emitEvent(TimerManagerDispatchEventEnum.createdManager, { success: true });
        TimerEvent.getInstance().subscribe(TimerEventEnum.timerTickLimit, this.notifyRelevantComponentsOfTimerIterationStopped);
        TimerEvent.getInstance().subscribe(TimerManagerSubscribeEventEnum.managerRemoveReference, (e) => {
            this.removeTimer(e.detail.namespace, e.detail.identifier, e.callback);
        });
    }

    public static getInstance(): TimerManager {
        if (!TimerManager.instance) {
            TimerManager.instance = new TimerManager();
        }

        return TimerManager.instance;
    }

    protected emitEvent = (name: TimerManagerDispatchEventEnum, detail: {} = {}): void => {
        const data: any = { ...detail };

        TimerEvent.getInstance().dispatch(name, {
            detail: data,
        });
    };

    protected retrieveIterator = (namespace: string): { [key: string]: Timer } => {
        if (!namespace) {
            throw Error('No iterator namespace supplied for referenced queue');
        }

        if (!Object.propertyIsEnumerable.call(window.timerHolder, namespace)) {
            window.timerHolder[namespace] = {};
        }

        return window.timerHolder[namespace];
    };

    protected identifierExists = (namespace: string, identifier: string): boolean => {
        const iterator = this.retrieveIterator(namespace);
        return Object.propertyIsEnumerable.call(iterator, identifier);
    };

    protected timerExists = (namespace: string, identifier: string, callback?: (e: any) => any): void => {
        let result = true;
        if (!this.identifierExists(namespace, identifier)) {
            result = false;
        }

        const response = {
            success: true,
            namespace: namespace,
            identifier: identifier,
            result: result,
        };
        this.emitEvent(TimerManagerDispatchEventEnum.timerExists, response);
        if (callback) {
            callback(response);
        }
    };

    protected timerRunning = (namespace: string, identifier: string, callback?: (e: any) => any): void => {
        let success = true;
        let result = { exists: true, running: false };
        if (!this.identifierExists(namespace, identifier)) {
            queueDebug(`TimerManager couldn't find timer ${identifier} in ${namespace}. Timer does not exist`);
            success = false;
            result.exists = false;
            result.running = false;
        }

        const iterator = this.retrieveIterator(namespace);

        if (success) {
            result.running = iterator[identifier].isStarted();
        }

        const response = { success: success, namespace: namespace, identifier: identifier, result: result };

        this.emitEvent(TimerManagerDispatchEventEnum.timerRunning, response);
        if (callback) {
            callback(response);
        }
    };

    protected timerExistsAndRunning = (namespace: string, identifier: string, callback?: (e: any) => any): void => {
        let existsResult = true;
        let runningResult = false;
        if (!this.identifierExists(namespace, identifier)) {
            existsResult = false;
        }

        if (existsResult) {
            const iterator = this.retrieveIterator(namespace);
            runningResult = iterator[identifier].isStarted();
        }

        const response = {
            success: true,
            namespace: namespace,
            identifier: identifier,
            result: { exists: existsResult, running: runningResult },
        };
        this.emitEvent(TimerManagerDispatchEventEnum.timerExistsAndRunning, response);
        if (callback) {
            callback(response);
        }
    };

    protected createTimer = (namespace: string, timerOptions: TimerOptions, callback?: (e: any) => any): void => {
        if (this.identifierExists(namespace, timerOptions.identifier)) {
            this.emitEvent(TimerManagerDispatchEventEnum.createdTimer, {
                success: false,
                identifier: timerOptions.identifier,
                namespace: namespace,
                reason: `Identifier ${timerOptions.identifier} already exists in ${namespace} namespace`,
            });
            queueDebug(`TimerManager couldn't add timer ${timerOptions.identifier} timer exists in namespace ${namespace}`);
            throw new Error('Identifier already exists');
        }
        const iterator = this.retrieveIterator(namespace);
        const tmpTimerInstance = new Timer(timerOptions, namespace);
        iterator[timerOptions.identifier] = tmpTimerInstance;
        const response = {
            success: true,
            identifier: timerOptions.identifier,
            namespace: namespace,
        };
        this.emitEvent(TimerManagerDispatchEventEnum.createdTimer, response);
        queueDebug(`TimerManager created timer ${tmpTimerInstance.getIdentifier()} in namespace ${namespace}.`);
        if (callback) {
            callback(response);
        }
    };

    protected startTimer = (namespace: string, identifier: string, callback?: (e: any) => any): void => {
        if (!this.identifierExists(namespace, identifier)) {
            this.emitEvent(TimerManagerDispatchEventEnum.startedTimer, {
                success: false,
                identifier: identifier,
                namespace: namespace,
                reason: `TimerManager couldn't start timer ${identifier} in namespace ${namespace}. Timer does not exist`,
            });
            queueDebug(`TimerManager couldn't start timer ${identifier} in namespace ${namespace}. Timer does not exist`);
            throw new Error(`Identifier ${identifier} does not exist in namespace ${namespace}`);
        }
        const iterator = this.retrieveIterator(namespace);
        const response = { success: true, identifier: identifier, namespace: namespace };
        iterator[identifier].start();
        this.emitEvent(TimerManagerDispatchEventEnum.startedTimer, response);
        queueDebug(`TimerManager started timer ${identifier} in namespace ${namespace}`);
        if (callback) {
            callback(response);
        }
    };

    protected startTimerWithDelay = (namespace: string, identifier: string, delay: number, callback?: (e: any) => any): void => {
        if (!this.identifierExists(namespace, identifier)) {
            this.emitEvent(TimerManagerDispatchEventEnum.startedTimer, {
                success: false,
                identifier: identifier,
                namespace: namespace,
                reason: `TimerManager couldn't start timer ${identifier} in namespace ${namespace}. Timer does not exist`,
            });
            queueDebug(`TimerManager couldn't start timer ${identifier} in namespace ${namespace}. Timer does not exist`);
            throw new Error(`Identifier ${identifier} does not exist in namespace ${namespace}`);
        }
        const iterator = this.retrieveIterator(namespace);
        const response = { success: true, identifier: identifier, namespace: namespace };
        iterator[identifier].start();
        this.emitEvent(TimerManagerDispatchEventEnum.startedTimer, response);
        queueDebug(`TimerManager started timer ${identifier} in namespace ${namespace}`);
        if (callback) {
            callback(response);
        }
    };

    protected stopTimer = (namespace: string, identifier: string, callback?: (e: any) => any): void => {
        if (!this.identifierExists(namespace, identifier)) {
            this.emitEvent(TimerManagerDispatchEventEnum.stoppedTimer, {
                success: false,
                identifier: identifier,
                namespace: namespace,
                reason: `TimerManager couldn't stop timer ${identifier} in namespace ${namespace}. Timer does not exist`,
            });
            queueDebug(`TimerManager couldn't stop timer ${identifier} in namespace ${namespace}. Timer does not exist`);
            throw new Error(`Identifier ${identifier} does not exist in namespace ${namespace}`);
        }
        const response = { success: true, identifier: identifier, namespace: namespace };
        const iterator = this.retrieveIterator(namespace);
        try {
            iterator[identifier].stop();
        } catch (error) {
            queueDebug(`Encountered error while trying to stop timer ${identifier} from namespace ${namespace} : ${error}`);
        }
        this.emitEvent(TimerManagerDispatchEventEnum.stoppedTimer, response);
        queueDebug(`TimerManager stopped timer ${identifier} in namespace ${namespace}`);
        if (callback) {
            callback(response);
        }
    };

    protected removeTimer = (namespace: string, identifier: string, callback?: (e: any) => any): void => {
        if (!this.identifierExists(namespace, identifier)) {
            queueDebug(`TimerManager couldn't stop timer ${identifier} in namespace ${namespace}. Timer does not exist`);
        }
        const response = { success: true, identifier: identifier, namespace: namespace };
        const iterator = this.retrieveIterator(namespace);
        this.emitEvent(TimerManagerDispatchEventEnum.stoppedTimer, response);
        try {
            iterator[identifier].stop();
        } catch (error) {
            queueDebug(`Encountered error while trying to stop timer ${identifier} from namespace ${namespace} : ${error}`);
        }
        queueDebug(`TimerManager stopped timer ${identifier} in namespace ${namespace}`);
        delete iterator[identifier];
        queueDebug(`TimerManager removed timer ${identifier} in namespace ${namespace}`);
        this.emitEvent(TimerManagerDispatchEventEnum.removedTimer, response);
        if (callback) {
            callback(response);
        }
    };

    protected stopTimersInNamespace = (namespace: string, callback?: (e: any) => any): void => {
        const iterator = this.retrieveIterator(namespace);
        for (const identifier in iterator) {
            try {
                iterator[identifier].stop();
            } catch (error) {
                queueDebug(`Encountered error while trying to stop timer ${identifier} from namespace ${namespace} : ${error}`);
            }
        }
        const response = { success: true, namespace: namespace };
        this.emitEvent(TimerManagerDispatchEventEnum.stoppedTimersInNamespace, response);
        queueDebug(`TimerManager stopped all timers in namespace ${namespace}`);
        if (callback) {
            callback(response);
        }
    };

    protected removeTimersInNamespace = (namespace: string, callback?: (e: any) => any): void => {
        const iterator = this.retrieveIterator(namespace);
        for (const identifier in iterator) {
            try {
                iterator[identifier].stop();
                delete iterator[identifier];
            } catch (error) {
                queueDebug(`Encountered error while trying to remove timer ${identifier} from namespace ${namespace} : ${error}`);
            }
        }
        const response = { success: true, namespace: namespace };
        this.emitEvent(TimerManagerDispatchEventEnum.removedTimersInNamespace, response);
        queueDebug(`TimerManager stopped and removed all timers in namespace ${namespace}`);
        if (callback) {
            callback(response);
        }
    };

    protected stopTimersGlobally = (callback?: (e: any) => any): void => {
        for (const namespace in window.timerHolder) {
            for (const identifier in window.timerHolder[namespace]) {
                try {
                    window.timerHolder[namespace][identifier].stop();
                } catch (error) {
                    queueDebug(`Encountered error while trying to stop timer ${error}`);
                }
            }
        }
        const response = { success: true };
        this.emitEvent(TimerManagerDispatchEventEnum.stoppedTimersGlobally, response);
        queueDebug(`TimerManager stopped and removed all timers`);
        if (callback) {
            callback(response);
        }
    };

    protected removeTimersGlobally = (callback?: (e: any) => any): void => {
        for (const namespace in window.timerHolder) {
            for (const identifier in window.timerHolder[namespace]) {
                try {
                    window.timerHolder[namespace][identifier].stop();
                    delete window.timerHolder[namespace][identifier];
                } catch (error) {
                    queueDebug(`Encountered error while trying to remove timer ${error}`);
                }
            }
        }
        const response = { success: true };
        this.emitEvent(TimerManagerDispatchEventEnum.removedTimersGlobally, response);
        queueDebug(`TimerManager stopped and removed all timers`);
        if (callback) {
            callback(response);
        }
    };

    protected notifyRelevantComponentsOfTimerIterationStopped = (e: TimerEventDict): void => {
        this.emitEvent(TimerManagerDispatchEventEnum.timerIterationsReached, {
            success: true,
            identifier: e.detail.identifier,
            namespace: e.detail.namespace,
        });
        queueDebug(`${e.detail.identifier} in namespace ${e.detail.namespace} reached it's iteration limit. Stopping timer.`);
    };

    protected accessMethodOnTimer = (
        namespace: string,
        identifier: string,
        method: string,
        methodArguments: Iterable<any> | null,
        callback?: (e: any) => any
    ): any => {
        const iterator = this.retrieveIterator(namespace);
        if (!this.identifierExists(namespace, identifier)) {
            throw new Error('Identifier does not exist');
        }

        const response = iterator[identifier].proxyMethod(method, methodArguments);

        if (callback) {
            callback(response);
        }

        this.emitEvent(TimerManagerDispatchEventEnum.accessedMethodOnTimer, {
            success: true,
            identifier: identifier,
            namespace: namespace,
            result: response,
        });
    };
}
